import { NAMESPACES } from '~/i18n/namespaces';
import type { TFuncKey } from 'react-i18next';
import type { TPlanCode } from '~/components/Membership/types/membership.types';
import type { AppStateKey } from '~/apps/main/App/hooks/useNavigation.consts';
import { OnboardingStepNames } from '~/components/IntentOnboarding/types/onboarding.types';

type TransKey = TFuncKey<[typeof NAMESPACES.GLOBALS, typeof NAMESPACES.HOME]>;

type TStepState = {
    route?: AppStateKey;
    title: TransKey;
    body: TransKey;
};

export interface IStepStaticProps {
    key: OnboardingStepNames;
    pending: TStepState;
    complete: TStepState;
}

const stepsKey: TransKey = 'home:setup_guide.mweb.steps';

const GALLERY_COMMON: Omit<TStepState, 'title'> = {
    body: `${stepsKey}.template_gallery.body`,
    route: 'root_core_navigation_flows_template_gallery'
} as const;

const SEND_TEST_COMMON = {
    title: `${stepsKey}.send_flow_test.title`,
    body: `${stepsKey}.send_flow_test.body`
} as const;

const ADD_LOGO_COMMON = {
    title: `${stepsKey}.add_logo.title`,
    body: `${stepsKey}.add_logo.body`,
    route: 'root_core_navigation_settings_company_general'
} as const;

const CONNECT_EMAIL_COMMON = {
    title: `${stepsKey}.connect_email.title`,
    body: `${stepsKey}.connect_email.body`
} as const;

const SEND_INVOICE_COMMON = {
    title: `${stepsKey}.send_invoice_new.title`,
    body: `${stepsKey}.send_invoice_new.body`
} as const;

const BANK_INFO_COMMON = {
    pending: {
        title: `${stepsKey}.bank_info.pending.title`,
        body: `${stepsKey}.bank_info.pending.body`,
        route: 'root_core_navigation_settings_company_bankInfo'
    },
    complete: {
        title: `${stepsKey}.bank_info.complete.title`,
        body: `${stepsKey}.bank_info.complete.body`,
        route: 'root_core_navigation_settings_company_general'
    }
} as const;

const Jan25JumpStartSteps: IStepStaticProps[] = [
    {
        key: 'add_logo',
        pending: ADD_LOGO_COMMON,
        complete: ADD_LOGO_COMMON
    },
    {
        key: 'connect_email',
        pending: CONNECT_EMAIL_COMMON,
        complete: CONNECT_EMAIL_COMMON
    },
    {
        key: 'send_invoice_new',
        pending: SEND_INVOICE_COMMON,
        complete: SEND_INVOICE_COMMON
    },
    {
        key: 'bank_info_new',
        ...BANK_INFO_COMMON
    }
];

const SUBSCRIPTION_ROUTE = 'root_core_navigation_settings_company_subscription';

export const getMwebSetupGuideStepData = (
    planCode: TPlanCode,
    isJan25JumpStart: boolean
): IStepStaticProps[] => {
    if (isJan25JumpStart) {
        return Jan25JumpStartSteps;
    }
    return [
        {
            key: 'template_gallery',
            pending: {
                title: `${stepsKey}.template_gallery.pending`,
                ...GALLERY_COMMON
            },
            complete: {
                title: `${stepsKey}.template_gallery.complete`,
                ...GALLERY_COMMON
            }
        },
        {
            key: 'subscribe',
            pending: {
                title: `${stepsKey}.subscribe.pending.title`,
                body: `${stepsKey}.subscribe.pending.body`,
                route: SUBSCRIPTION_ROUTE
            },
            complete: {
                title: `${stepsKey}.subscribe.complete.title`,
                body: `globals:pricing_plans.${planCode}`,
                route: SUBSCRIPTION_ROUTE
            }
        },
        {
            key: 'bank_info',
            ...BANK_INFO_COMMON
        },
        {
            key: 'send_flow_test',
            pending: SEND_TEST_COMMON,
            complete: SEND_TEST_COMMON
        }
    ];
};
